/**
 * Singleton containing all config settings of app.
 * These are all managed by Environment variables. Don't access env variables directly from code --
 * access them from here whereever possible.
 *
 * Usage:
 *  import AppSettings from '../utils/AppSettings'
 *
 *  console.log(AppSettings.awsSecretsName)
 */
class AppSettings {
  /**
   * Used to map the the secret name in AWS Secrets Manager
   */
  public awsSecretsName: string

  /**
   * Full URL, including API key, used to connect to the provider (Alchemy, Infura, etc)
   */
  public fullJsonRpcProviderUrl: string

  /**
   * Address of the Arpeggi Sounds contract
   */
  public arpeggiSoundsContractAddress: string

  /**
   * Address of the Arpeggi Songs contract
   */
  public arpeggiSongsContractAddress: string

  /**
   * Address of the ARP contract
   */
  public arpContractAddress: string

  /**
   * The name of the environment in which this application is running
   */
  public env: string

  /**
   * The name of the render status table used to store the render status entities
   */
  public renderStatusTableName: string

  /**
   * The name of the render status archive table used to store the archived render status entities
   */
  public renderStatusArchiveTableName: string

  /**
   * The name of the table used to store invocation data for the Onchain Event Listener
   */
  public invocationsTableName: string

  /**
   * The name of the table used to store the pending transaction entities
   */
  public pendingTransactionsTableName: string

  /**
   * The name of the table used to store the completed transaction entities
   */
  public completedTransactionsTableName: string

  /**
   * The name of the table used to store any failed transactions
   */
  public failedTransactionsTableName: string

  /**
   * The name of the table used to store the minting errors
   */
  public mintErrorTableName: string

  /**
   * The log level used by the application
   */
  public pinoLogLevel: string

  /**
   * The eth gas station URL used to fetch gas info
   */
  public gasStationFeeDataUrl: string

  /**
   * The URL of the API in this environment
   */
  public appUrl: string

  /**
   * The upload mode used when uploading rendered audio. s3, arweave, or bundlr
   */
  public mintUploadMode: any

  /**
   * The URL of the Bundlr node
   */
  public bundlrNodeUrl: string

  /**
   * The currency to use when paying for Bundlr transactions
   * eg: matic
   */
  public bundlrCurrency: string

  /**
   * Full URL to use for the DynamoDB endpoint
   * NOTE: This is only used by the Onchain Event Listener
   * TODO: Depreciate this
   */
  public awsDynamodbEndpointUrl: string

  /**
   * The name of the blockchain
   * eg: MUMBAI, POLYGONMAINNET
   */
  public chainName: string

  /**
   * Flag to drive if we are in local dev
   * NOTE: This is only used by the Onchain Event Listener
   * TODO: Depreciate this
   */
  public isLocalDev: string

  /**
   * Depreciated
   * TODO: Remove this
   */
  public maxBlockRange: string

  /**
   * The maximum number of media the Onchain Event Listener will
   * parse during a single invocation
   */
  public maxNumMediaToParsePerInvocation: string

  /**
   * The max priority fee multiplier used to calculate the priority fee
   * after a transaction fails to be mined within the tolerance
   * specified in the Transaction Shepard lambda
   *
   * Default value: 125
   */
  public maxPriorityFeeMultiplier: number

  /**
   * The chain id of the blockchain
   */
  public chainId: number

  /**
   * The address of the Arweave wallet used by the gasless minting flow
   */
  public arweaveAddress: string

  /**
   * The address of the Polygon wallet used by the gasless minting flow
   */
  public polygonAddress: string

  /**
   * The name of the AWS secret
   */
  public polygonProviderUrlSecretName: string

  /**
   * The maximum number of times that the shepard will check a transaction
   * to see if it has been mined, prior to rebroadcasting
   *
   * Defaults to 3
   *
   * eg: Set to 4. The shepard will check to see if a transaction has been mined
   * 4 times. If, after checking the 4th time, it has not been mined. the shepard
   * will begin the rebroadcast flow
   */
  public maxShepardMinedChecks: number

  /**
   * The maximum number of a times a given pending transaction is allowed to
   * be rebroadcast. If this limit is exceeded, minting stops across the platform.
   *
   * Defaults to 5
   */
  public maxRebroadcastsAllowed: number

  /**
   * The maximum number of times the render upload service will try to upload a wav file
   * Default value: 3
   */
  public uploadWavFileRetries: number

  /**
   * Full connection string for the postgres database.
   * In the API, this gets set in the task-definition.
   * In the lambdas, this gets overridden by SecretsClient at runtime (see `databaseUrlSecretArn`)
   */
  public databaseUrl: string

  /**
   * The full ARN of the secret in AWS SecretsManager which contains the conection string for postgres. Used by lambdas to overwrite `databaseUrl`.
   */
  public databaseUrlSecretArn: string

  /**
   * The arn of the secret for the LaunchDarkly API key.
   * In the API, this gets set in the task-definition.
   * In the lambdas, this gets overridden by SecretsClient at runtime (see `launchDarklyApiKeySecretArn`)
   */
  public launchDarklyApiKey: string

  /**
   * The full ARN of the secret in AWS SecretsManager which contains the LaunchDarkly API Key. Used by lambdas to overwrite `launchDarklyApiKey`.
   */
  public launchDarklyApiKeySecretArn: string

  public constructor() {
    this.awsSecretsName = process.env.AWS_SECRETS_NAME || ''
    this.fullJsonRpcProviderUrl = process.env.FULL_JSON_RPC_PROVIDER_URL || ''
    this.arpeggiSoundsContractAddress = process.env.ARPEGGI_SOUNDS_CONTRACT_ADDRESS || ''
    this.arpeggiSongsContractAddress = process.env.ARPEGGI_SONGS_CONTRACT_ADDRESS || ''
    this.arpContractAddress = process.env.ARP_CONTRACT_ADDRESS || ''
    this.env = process.env.ENV || ''
    this.renderStatusTableName = process.env.RENDER_STATUS_TABLE_NAME || ''
    this.renderStatusArchiveTableName = process.env.RENDER_STATUS_ARCHIVE_TABLE_NAME || ''
    this.pinoLogLevel = process.env.PINO_LOG_LEVEL || ''
    this.gasStationFeeDataUrl = process.env.GAS_STATION_FEE_DATA_URL || ''
    this.appUrl = process.env.APP_URL || ''
    this.mintUploadMode = process.env.MINT_UPLOAD_MODE ? process.env.MINT_UPLOAD_MODE : 's3'
    this.bundlrNodeUrl = process.env.BUNDLR_NODE_URL || ''
    this.bundlrCurrency = process.env.BUNDLR_CURRENCY || ''
    this.awsDynamodbEndpointUrl = process.env.AWS_DYNAMODB_ENDPOINT_URL || ''
    this.chainName = process.env.CHAIN_NAME || ''
    this.isLocalDev = process.env.IS_LOCAL_DEV || ''
    this.maxBlockRange = process.env.MAX_BLOCKRANGE || ''
    this.maxNumMediaToParsePerInvocation = process.env.MAX_NUM_MEDIA_TO_PARSE_PER_INVOCATION || ''
    this.maxPriorityFeeMultiplier = process.env.MAX_PRIORITY_FEE_MULTIPLIER
      ? parseInt(process.env.MAX_PRIORITY_FEE_MULTIPLIER)
      : 125
    this.chainId = process.env.CHAIN_ID ? parseInt(process.env.CHAIN_ID) : 80001
    this.maxShepardMinedChecks = process.env.MAX_SHEPARD_MINED_CHECKS
      ? parseInt(process.env.MAX_SHEPARD_MINED_CHECKS)
      : 3
    this.maxRebroadcastsAllowed = process.env.MAX_REBROADCASTS_ALLOWED
      ? parseInt(process.env.MAX_REBROADCASTS_ALLOWED)
      : 5
    this.uploadWavFileRetries = process.env.UPLOAD_WAV_RETRIES
      ? parseInt(process.env.UPLOAD_WAV_RETRIES)
      : 3
    this.pendingTransactionsTableName = process.env.PENDING_TRANSACTIONS_TABLENAME || ''
    this.completedTransactionsTableName = process.env.COMPLETED_TRANSACTIONS_TABLENAME || ''
    this.failedTransactionsTableName = process.env.FAILED_TRANSACTIONS_TABLENAME || ''
    this.mintErrorTableName = process.env.MINT_ERROR_TABLENAME || ''
    this.databaseUrl = process.env.DATABASE_URL || ''
    this.databaseUrlSecretArn = process.env.DATABASE_URL_SECRET_ARN || ''
    this.launchDarklyApiKey = process.env.LAUNCHDARKLY_API_KEY || ''
    this.launchDarklyApiKeySecretArn = process.env.LAUNCHDARKLY_API_KEY_ARN || ''
  }
  /**
   * Used to override the default values in AppSettings
   *
   * Usage:
   *  const config = {
   *    databaseUrl: 'some-super-secret-connection-string'
   *  }
   *  AppSettings.overrideConfig(config)
   *
   * @param config Object with keys named exactly like the properties to update in AppSettings
   *
   */
  overrideConfig(config: Object) {
    const props = Object.entries(config)
    for (let i = 0; i < props.length; i++) {
      this[props[i][0]] = config[props[i][0]]
    }
  }

  // private getValue<T, K extends keyof T>(data: T, key: K) {
  //   return data[key]
  // }
}

export default new AppSettings()
